// src/services/aeo/changeRequests.ts
//
// The staff Requests page and the Delivery board's requests column, on the
// live change-requests API (ourteam-frontend#4580, BE epic ourteam-backend#6275).
// Staff only. There is no fixture fallback: a build either reads the server or
// says it could not.
//
// Every call answers a result instead of throwing: `{ ok: true, data }` or
// `{ ok: false, message }`, where `message` is the server's own sentence when it
// sent one. A failed read is never an empty list; the screens show Try again.
//
// Routes (all under /api/v1):
//   GET    /change-requests?status=&businessId=&limit=&page=   → rows + meta.counts + meta.pagination
//   POST   /change-requests/uploads          (multipart `file`)  → { fileKey }
//   POST   /change-requests/read/estimate    { text, fileKeys }  → { choose, default, options[], budget } (#6685)
//   POST   /change-requests/read             { text, fileKeys, engine? }  → { drafts, costUsd, readId }
//   POST   /change-requests/batch            { source, original, items } → { created, merged }
//   PATCH  /change-requests/{id}             { status?, title?, due?, owner?, … } → row
//   POST   /change-requests/{id}/reply       { body, fileKeys? } → row (fileKeys: reply screenshots, handoff)
//   GET    /change-requests/unmatched        → emails to oura@ with no client
//   POST   /change-requests/unmatched/{id}/assign { businessId } → the email
//   GET    /businesses                       → the client list, for the pickers

import axios, { type AxiosError } from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const AEO_BASE = `${import.meta.env.VITE_TALENT_AI_BE_BASE_URL}/api/v1`;
const FALLBACK = "Oura could not be reached. Try again.";

export type ChangeRequestStatus = "open" | "in_progress" | "waiting_on_client" | "answered" | "done";
export const CHANGE_REQUEST_STATUSES: ChangeRequestStatus[] = ["open", "in_progress", "waiting_on_client", "answered", "done"];
/** Where a request came from: `oura` is a client's own request, the rest are staff-added. */
export type ChangeRequestSource = "oura" | "text" | "screenshot" | "email";
/** The sources `/batch` accepts: staff add, the client never does. */
export type BatchSource = Exclude<ChangeRequestSource, "oura">;
export type ChangeRequestCounts = Record<ChangeRequestStatus, number>;

export interface ChangeRequestFile {
    fileKey: string;
    name: string;
    url: string;
}

export interface ChangeRequestOriginal {
    text: string;
    fileKeys: string[];
    email: { from: string; subject: string; messageId: string } | null;
}

/** One request as the server stores it. PATCH, reply and batch answer this shape. */
export interface ChangeRequest {
    id: string;
    businessId: string;
    requestedBy: string;
    requesterEmail: string | null;
    note: string;
    files: ChangeRequestFile[];
    status: ChangeRequestStatus;
    /** `files`: screenshots sent with the reply (absent on an older server). */
    reply: { body: string; by: string; at: string; files?: ChangeRequestFile[] } | null;
    /** Every reply, oldest first; `reply` is the last (#6874). Absent on an older server. */
    replies?: Array<{ body: string; by: string; at: string; files?: ChangeRequestFile[] }>;
    smallChange: unknown | null;
    at: string;
    doneAt: string | null;
    doneBy: string | null;
    source: ChangeRequestSource;
    title: string;
    where: string | null;
    /** YYYY-MM-DD. */
    due: string | null;
    owner: { userId: string; name: string } | null;
    original: ChangeRequestOriginal | null;
    askedAgain: { at: string; original: ChangeRequestOriginal | null }[];
}

/** A row from the list: the request, and the client it belongs to. */
export interface ChangeRequestRow extends ChangeRequest {
    business: { id: string; name: string; domain: string };
}

export interface ChangeRequestPagination {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
}

export interface ChangeRequestList {
    rows: ChangeRequestRow[];
    counts: ChangeRequestCounts;
    pagination: ChangeRequestPagination;
}

export interface ChangeRequestDraft {
    title: string;
    ask: string;
    who: string | null;
    where: string;
    due: string | null;
    businessId: string | null;
    repeatOf: string | null;
}

export interface ChangeRequestItem {
    title: string;
    ask?: string;
    who?: string;
    where?: string;
    due?: string | null;
    businessId: string;
    repeatOf?: string | null;
}

export interface ChangeRequestPatch {
    status?: ChangeRequestStatus;
    title?: string;
    due?: string | null;
    owner?: { userId: string } | null;
    smallChange?: boolean;
    confirmOverLimit?: boolean;
}

export interface UnmatchedEmail {
    id: string;
    messageId: string;
    from: string;
    subject: string;
    text: string;
    files: { fileKey: string; url: string }[];
    receivedAt: string | null;
    businessId: string | null;
    state: "unmatched" | "failed";
    requestIds: string[];
    error: string | null;
    assignedBy: string | null;
    at: string | null;
}

export interface RequestClient {
    id: string;
    name: string;
}

export type ChangeRequestResult<T> = { ok: true; data: T } | { ok: false; message: string };

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/** A FormData upload must not carry the JSON content type authHeaders() sets, so it has its own headers. */
function multipartAuthHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function ok<T>(data: T): ChangeRequestResult<T> {
    return { ok: true, data };
}

function failed<T>(err: unknown, what: string): ChangeRequestResult<T> {
    const body = (err as AxiosError<{ message?: unknown }>)?.response?.data;
    const message = typeof body?.message === "string" && body.message.trim() ? body.message : FALLBACK;
    catchErrorLog(`[change-requests] ${what} failed: ${err}`);
    return { ok: false, message };
}

/** The rows, counts and pages of the staff list. A reply without counts is a failed read, not an empty one. */
export async function listChangeRequests(q: {
    status?: ChangeRequestStatus;
    businessId?: string;
    limit?: number;
    page?: number;
}): Promise<ChangeRequestResult<ChangeRequestList>> {
    try {
        const res = await axios.get(`${AEO_BASE}/change-requests`, {
            headers: authHeaders(),
            params: { status: q.status, businessId: q.businessId, limit: q.limit, page: q.page },
        });
        const counts = res.data?.meta?.counts;
        const pagination = res.data?.meta?.pagination;
        if (!counts || !pagination || !Array.isArray(res.data?.data)) {
            return { ok: false, message: FALLBACK };
        }
        return ok({ rows: res.data.data, counts, pagination });
    } catch (err) {
        return failed(err, "list");
    }
}

/** One screenshot, stored for the reader and for the request it becomes. PNG, JPEG or WebP, up to 10 MB. */
export async function uploadChangeRequestImage(file: File): Promise<ChangeRequestResult<{ fileKey: string }>> {
    try {
        const form = new FormData();
        form.append("file", file, file.name);
        const res = await axios.post(`${AEO_BASE}/change-requests/uploads`, form, { headers: multipartAuthHeaders() });
        const fileKey = res.data?.data?.fileKey;
        if (typeof fileKey !== "string" || !fileKey) return { ok: false, message: FALLBACK };
        return ok({ fileKey });
    } catch (err) {
        return failed(err, "upload");
    }
}

/** Which model reads the message (ourteam-backend#6685): Gemini through Vertex, or Claude when the server offers it. */
export type ReadEngine = "gemini" | "claude";

/** One model staff can pick, with what this message would cost on it. */
export interface ReadEngineOption {
    engine: ReadEngine;
    label: string;
    model: string;
    /** False: shown greyed with `reason` (no key, no prices). */
    available: boolean;
    reason: string | null;
    inputTokens: number | null;
    /** False: the provider could not count, so the input is guessed from the length. */
    inputCounted: boolean;
    outputTokens: number | null;
    /** The typical cost; null while the model has no price. */
    estimateUsd: number | null;
    /** The most it could cost: two calls at the answer limit. */
    maxUsd: number | null;
    /** The month is open and `maxUsd` fits in what is left of it. */
    withinBudget: boolean;
}

export interface ReadBudget {
    spentUsd: number | null;
    capUsd: number;
    reads: number;
    readsCap: number;
    /** True: the USD cap decides; false: the read count does. */
    priced: boolean;
    open: boolean;
}

export interface ReadEstimate {
    /** False: one model only, nothing to choose; read straight away. */
    choose: boolean;
    default: ReadEngine;
    options: ReadEngineOption[];
    budget: ReadBudget;
}

/** What each model would cost to read this message, before reading it. Writes nothing and costs nothing.
 *  `null`: the server has no estimate route (from before ourteam-backend#6685), so there is no choice to offer. */
export async function estimateChangeRequestRead(input: { text: string; fileKeys: string[] }): Promise<ChangeRequestResult<ReadEstimate | null>> {
    try {
        const res = await axios.post(`${AEO_BASE}/change-requests/read/estimate`, input, { headers: authHeaders() });
        const data = res.data?.data as ReadEstimate | undefined;
        if (!data || !Array.isArray(data.options)) return { ok: false, message: FALLBACK };
        return ok(data);
    } catch (err) {
        if ((err as AxiosError)?.response?.status === 404) return ok(null);
        return failed(err, "estimate");
    }
}

/** Reads a paste and/or screenshots into drafts, on `engine` when staff chose one. Writes nothing: staff add the checked ones through addChangeRequests. */
export async function readChangeRequests(input: { text: string; fileKeys: string[]; engine?: ReadEngine }): Promise<ChangeRequestResult<{ drafts: ChangeRequestDraft[]; readId: string }>> {
    try {
        const res = await axios.post(`${AEO_BASE}/change-requests/read`, input, { headers: authHeaders() });
        const drafts = res.data?.data?.drafts;
        if (!Array.isArray(drafts)) return { ok: false, message: FALLBACK };
        return ok({ drafts, readId: String(res.data?.data?.readId ?? "") });
    } catch (err) {
        return failed(err, "read");
    }
}

/** The checked drafts, as requests. A draft with `repeatOf` is merged onto that request instead of a new row. */
export async function addChangeRequests(input: {
    source: BatchSource;
    original: { text: string; fileKeys: string[]; email: null };
    items: ChangeRequestItem[];
}): Promise<ChangeRequestResult<{ created: ChangeRequest[]; merged: ChangeRequest[] }>> {
    try {
        const res = await axios.post(`${AEO_BASE}/change-requests/batch`, input, { headers: authHeaders() });
        const created = res.data?.data?.created;
        const merged = res.data?.data?.merged;
        if (!Array.isArray(created) || !Array.isArray(merged)) return { ok: false, message: FALLBACK };
        return ok({ created, merged });
    } catch (err) {
        return failed(err, "batch");
    }
}

/** Status, title, due or owner. Only the keys sent change. The answer has no `business`: merge it into the row you hold. */
export async function patchChangeRequest(id: string, body: ChangeRequestPatch): Promise<ChangeRequestResult<ChangeRequest>> {
    try {
        const res = await axios.patch(`${AEO_BASE}/change-requests/${encodeURIComponent(id)}`, body, { headers: authHeaders() });
        return ok(res.data?.data as ChangeRequest);
    } catch (err) {
        return failed(err, "patch");
    }
}

/** A staff reply. The server sets the status to answered unless the request is done. */
export async function replyToChangeRequest(id: string, body: string, fileKeys: string[] = []): Promise<ChangeRequestResult<ChangeRequest>> {
    try {
        const payload = fileKeys.length > 0 ? { body, fileKeys } : { body };
        const res = await axios.post(`${AEO_BASE}/change-requests/${encodeURIComponent(id)}/reply`, payload, { headers: authHeaders() });
        return ok(res.data?.data as ChangeRequest);
    } catch (err) {
        return failed(err, "reply");
    }
}

/** Emails to oura@ that no single client matched, or whose read failed. Newest first. */
export async function listUnmatchedEmails(): Promise<ChangeRequestResult<UnmatchedEmail[]>> {
    try {
        const res = await axios.get(`${AEO_BASE}/change-requests/unmatched`, { headers: authHeaders() });
        const rows = res.data?.data;
        if (!Array.isArray(rows)) return { ok: false, message: FALLBACK };
        return ok(rows);
    } catch (err) {
        return failed(err, "unmatched");
    }
}

/** Staff pick the client for an email. The email's requests are filed from its text. */
export async function assignUnmatchedEmail(id: string, businessId: string): Promise<ChangeRequestResult<null>> {
    try {
        await axios.post(`${AEO_BASE}/change-requests/unmatched/${encodeURIComponent(id)}/assign`, { businessId }, { headers: authHeaders() });
        return ok(null);
    } catch (err) {
        return failed(err, "assign");
    }
}

/** The clients the pickers offer. Read straight from the server: a failed read is an error, never an empty list. */
export async function listRequestClients(): Promise<ChangeRequestResult<RequestClient[]>> {
    try {
        const res = await axios.get(`${AEO_BASE}/businesses`, { headers: authHeaders() });
        const body = res.data?.data ?? res.data;
        if (!Array.isArray(body?.businesses)) return { ok: false, message: FALLBACK };
        return ok(body.businesses.map((b: { id: string; business_name?: string; domain?: string }) => ({ id: b.id, name: b.business_name || b.domain || b.id })));
    } catch (err) {
        return failed(err, "clients");
    }
}
